import React, { useEffect, useState } from 'react';
import img1 from '../../Image/AdminPro.svg';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiMail, FiPhone, FiMapPin, FiEdit3, FiSave, FiX, FiMap, FiHome } from 'react-icons/fi';

const Profile = () => {
    const [loader, setLoader] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate()
    const [user, setUser] = useState({
        a_email: "",
        a_name: "",
        a_state: "",
        a_city: "",
        a_address: "",
        a_phone: "",
    });

    const handleInputChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value })
    }

    const handleEditClick = async () => {
        if (isEditing) {
            setIsEditing(!isEditing);
            setLoader(true)
            try {
                const body = {
                    a_name: user?.a_name,
                    a_state: user?.a_state,
                    a_city: user?.a_city,
                    a_phone: user?.a_phone,
                    a_address: user?.a_address
                }
                const resp = await Calling.AdminProfileUpdate(body);
                const { status, data, message } = resp;
                if (status) {
                    setUser(data);
                    toast.success(message);
                } else {
                    toast.error(message);
                }
            } catch (error) {
                navigate('/Error');
            } finally {
                setLoader(false)
            }
        } else { setIsEditing(!isEditing); }

    };

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true)
            try {
                const resp = await Calling.GetAdminData();
                const { status, data, message } = resp;
                if (status) {
                    setUser(data);
                } else {
                    toast.error(message);
                }
            } catch (error) {
                navigate('/Error');
            } finally {
                setLoader(false)
            }
        }
        fetchData()
        // eslint-disable-next-line
    }, [])

    const InputField = ({ icon: Icon, label, name, value, disabled = false, colSpan = false }) => (
        <div className={`${colSpan ? 'md:col-span-2' : ''}`}>
            <label className="flex items-center gap-2 text-sm font-medium text-main-subfont mb-2">
                <Icon size={16} />
                {label}
            </label>
            <input
                type="text"
                name={name}
                value={value || ''}
                disabled={disabled}
                readOnly={!isEditing || disabled}
                onChange={handleInputChange}
                maxLength={name === 'a_phone' ? 11 : undefined}
                minLength={name === 'a_phone' ? 9 : undefined}
                className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 outline-none
                    ${disabled
                        ? 'bg-main-bg border-main-border text-main-subfont cursor-not-allowed'
                        : isEditing
                            ? 'bg-blue-50 border-blue-400 text-main-font focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                            : 'bg-main-bg border-main-border text-main-font'
                    }`}
            />
        </div>
    );

    return (
        <>
            {loader ? <Loader /> :
                <div className="w-full min-h-screen text-main-font">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold text-main-font">My Profile</h1>
                        <p className="text-sm text-main-subfont mt-1">Manage your account information</p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-6">
                        {/* Profile Card */}
                        <div className="w-full lg:w-80 shrink-0">
                            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                                {/* Header Background */}
                                <div className="h-24 bg-gradient-to-r from-blue-500 to-blue-600 relative">
                                    <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                                        <div className="w-24 h-24 rounded-2xl bg-main-white p-2 shadow-lg border-4 border-main-white">
                                            <img
                                                src={img1}
                                                alt="Profile"
                                                className="w-full h-full object-contain rounded-xl"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Profile Info */}
                                <div className="pt-16 pb-6 px-6 text-center">
                                    <h2 className="text-xl font-bold text-main-font">{user?.a_name || 'Admin'}</h2>
                                    <p className="text-sm text-main-subfont mt-1">{user?.a_email}</p>

                                    <div className="mt-6 pt-6 border-t border-main-border">
                                        <div className="flex items-center justify-center gap-2 text-main-subfont">
                                            <FiMapPin size={16} />
                                            <span className="text-sm">
                                                {user?.a_city && user?.a_state
                                                    ? `${user.a_city}, ${user.a_state}`
                                                    : 'Location not set'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Quick Stats */}
                                    <div className="mt-6 grid grid-cols-2 gap-3">
                                        <div className="p-3 rounded-xl bg-main-th">
                                            <div className="text-lg font-bold text-main-blue">Admin</div>
                                            <div className="text-xs text-main-subfont">Role</div>
                                        </div>
                                        <div className="p-3 rounded-xl bg-blue-50">
                                            <div className="text-lg font-bold text-blue-600">Active</div>
                                            <div className="text-xs text-main-subfont">Status</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details Card */}
                        <div className="flex-1">
                            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                                {/* Card Header */}
                                <div className="p-6 border-b border-main-border flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-main-font">Account Details</h3>
                                        <p className="text-sm text-main-subfont mt-0.5">
                                            {isEditing ? 'Edit your information below' : 'View and manage your profile'}
                                        </p>
                                    </div>
                                    {isEditing && (
                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                                            Editing
                                        </span>
                                    )}
                                </div>

                                {/* Form Fields */}
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <InputField
                                            icon={FiMail}
                                            label="Email Address"
                                            name="a_email"
                                            value={user?.a_email}
                                            disabled={true}
                                        />
                                        <InputField
                                            icon={FiUser}
                                            label="Full Name"
                                            name="a_name"
                                            value={user?.a_name}
                                        />
                                        <InputField
                                            icon={FiMap}
                                            label="State"
                                            name="a_state"
                                            value={user?.a_state}
                                        />
                                        <InputField
                                            icon={FiMapPin}
                                            label="City"
                                            name="a_city"
                                            value={user?.a_city}
                                        />
                                        <InputField
                                            icon={FiPhone}
                                            label="Phone Number"
                                            name="a_phone"
                                            value={user?.a_phone}
                                        />
                                        <InputField
                                            icon={FiHome}
                                            label="Address"
                                            name="a_address"
                                            value={user?.a_address}
                                            colSpan={true}
                                        />
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="px-6 py-4 bg-main-bg border-t border-main-border flex justify-end gap-3">
                                    {isEditing && (
                                        <button
                                            onClick={() => setIsEditing(false)}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-main-font bg-main-white border border-main-border hover:bg-main-th transition-colors"
                                        >
                                            <FiX size={18} />
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        onClick={handleEditClick}
                                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-main-white transition-all
                                            ${isEditing
                                                ? 'bg-main-blue hover:bg-main-blue/90'
                                                : 'bg-blue-500 hover:bg-blue-600'
                                            }`}
                                    >
                                        {isEditing ? (
                                            <>
                                                <FiSave size={18} />
                                                Save Changes
                                            </>
                                        ) : (
                                            <>
                                                <FiEdit3 size={18} />
                                                Edit Profile
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Info Note */}
                            <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100">
                                <p className="text-sm text-blue-700">
                                    <span className="font-medium">Note:</span> Email address cannot be changed. Contact support if you need to update your email.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </>
    );
}

export default Profile;